import type { ComponentType } from "react";
import { Tag } from "antd";
import { FaHeartPulse } from "react-icons/fa6";
import {
	GiAmericanFootballBall,
	GiAmericanFootballHelmet,
	GiBackPain,
	GiBarefoot,
	GiBiceps,
	GiBrokenBone,
	GiElbowPad,
	GiFootPlaster,
	GiHand,
	GiHealthNormal,
	GiJoint,
	GiKneeCap,
	GiLeg,
} from "react-icons/gi";
import { GrYoga } from "react-icons/gr";
import { MdPersonalInjury } from "react-icons/md";
import type { RtpTemplate } from "./types";

// The backend accepts only these icon keys. An unknown key uses the general icon.
const TEMPLATE_ICONS: Record<string, ComponentType> = {
	helmet: GiAmericanFootballHelmet,
	elbow: GiElbowPad,
	hand: GiHand,
	back: GiBackPain,
	joint: GiJoint,
	leg: GiLeg,
	knee: GiKneeCap,
	ankle: GiFootPlaster,
	foot: GiBarefoot,
	bone: GiBrokenBone,
	muscle: GiBiceps,
	shoulder: MdPersonalInjury,
	general: GiHealthNormal,
	stretch: GrYoga,
	football: GiAmericanFootballBall,
	cardio: FaHeartPulse,
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`;

const formatDuration = (days: number): string =>
	days % 7 === 0 ? plural(days / 7, "Week") : plural(days, "Day");

const templateChips = (template: RtpTemplate): string[] => {
	const { phases } = template.details;
	const totalDays = phases.reduce((sum, phase) => sum + phase.durationDays, 0);
	const criteriaCount = phases.reduce((sum, phase) => sum + phase.criteria.length, 0);
	return [plural(phases.length, "Phase"), formatDuration(totalDays), `${criteriaCount} Exit Criteria`];
};

interface TemplateLibraryPaneProps {
	templates: RtpTemplate[];
	selectedKey: string | null;
	onSelect: (key: string) => void;
}

export const TemplateLibraryPane = ({ templates, selectedKey, onSelect }: TemplateLibraryPaneProps) => (
	<div className="rtp-wz-pane rtp-wz-tpl-grid" role="radiogroup" aria-label="Injury template">
		{templates.map((template) => {
			const selected = selectedKey === template.key;
			const Icon = TEMPLATE_ICONS[template.icon] ?? TEMPLATE_ICONS.general;
			return (
				<div
					key={template.key}
					role="radio"
					aria-checked={selected}
					tabIndex={0}
					className={`rtp-wz-tpl-card${selected ? " selected" : ""}`}
					onClick={() => onSelect(template.key)}
					onKeyDown={(e) => {
						if (e.key !== "Enter" && e.key !== " ") return;
						e.preventDefault();
						onSelect(template.key);
					}}
				>
					<div className="rtp-wz-tpl-icon">
						<Icon />
					</div>
					<div className="rtp-wz-tpl-body">
						<div className="rtp-wz-tpl-name">{template.name}</div>
						<div className="rtp-wz-tpl-summary">{template.summary}</div>
						<div className="rtp-wz-tpl-chips">
							{templateChips(template).map((chip) => (
								<Tag key={chip} bordered={false}>
									{chip}
								</Tag>
							))}
						</div>
					</div>
				</div>
			);
		})}
	</div>
);
